import moment from 'moment';
import d from './DOM.js';
import network from './network.js';
import AccountManager from './accountManager.js';

export default abstract class Calendar {
	private static currentView: 'month' | 'week' = 'month';
	private static currentDate: moment.Moment;
	private static dailyData: any[] = [];
	private static startingBalance: number = 0;
	private static balanceAdjustments: any[] = [];

	public static async init() {
		const config = (window as any).calendarData;
		if (!config) { return; }

		Calendar.currentView = (config.view as 'month' | 'week') || 'month';
		Calendar.currentDate = moment(config.initialDate);

		// Controls
		const prevBtn = d.get<HTMLButtonElement>('prev-btn');
		const nextBtn = d.get<HTMLButtonElement>('next-btn');
		const monthBtn = d.get<HTMLButtonElement>('view-month-btn');
		const weekBtn = d.get<HTMLButtonElement>('view-week-btn');
		const monthSelect = d.get<HTMLSelectElement>('month-select');
		const yearSelect = d.get<HTMLSelectElement>('year-select');

		if (prevBtn) { prevBtn.addEventListener('click', () => Calendar.navigate(-1)); }
		if (nextBtn) { nextBtn.addEventListener('click', () => Calendar.navigate(1)); }
		if (monthBtn) { monthBtn.addEventListener('click', () => Calendar.switchView('month')); }
		if (weekBtn) { weekBtn.addEventListener('click', () => Calendar.switchView('week')); }

		if (monthSelect) {
			monthSelect.value = Calendar.currentDate.month().toString();
			monthSelect.addEventListener('change', () => Calendar.handleSelectChange());
		}

		if (yearSelect) {
			const currentYear = moment().year();
			for (let i = currentYear - 5; i <= currentYear + 1; i++) {
				const opt = document.createElement('option');
				opt.value = i.toString();
				opt.text = i.toString();
				yearSelect.appendChild(opt);
			}
			yearSelect.value = Calendar.currentDate.year().toString();
			yearSelect.addEventListener('change', () => Calendar.handleSelectChange());
		}

		await Calendar.loadData();
		Calendar.updateActiveViewButton();
	}

	private static async loadData() {
		const start = Calendar.currentView === 'month' ? 
			moment(Calendar.currentDate).startOf('month').startOf('week') : 
			moment(Calendar.currentDate).startOf('week');
		
		const end = Calendar.currentView === 'month' ? 
			moment(Calendar.currentDate).endOf('month').endOf('week') : 
			moment(Calendar.currentDate).endOf('week');

		const accountNumber = AccountManager.currentAccount?.accountNumber;
		if (!accountNumber) { return; }

		const res = await network.ag(`/api/calendar/data?accountNumber=${accountNumber}&startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}`);
		
		if (res.ok) {
			Calendar.dailyData = res.payload.dailyPnL;
			Calendar.startingBalance = res.payload.startingBalance;
			Calendar.balanceAdjustments = res.payload.rangeBalanceAdjustments;
			Calendar.render();
		}
	}

	private static navigate(direction: number) {
		if (Calendar.currentView === 'month') {
			Calendar.currentDate.add(direction, 'month');
		} else {
			Calendar.currentDate.add(direction, 'week');
		}
		Calendar.updateSelectors();
		Calendar.loadData();
	}

	private static switchView(view: 'month' | 'week') {
		Calendar.currentView = view;
		Calendar.updateActiveViewButton();
		Calendar.loadData();
	}

	private static handleSelectChange() {
		const monthSelect = d.get<HTMLSelectElement>('month-select');
		const yearSelect = d.get<HTMLSelectElement>('year-select');
		if (!monthSelect || !yearSelect) { return; }

		const month = parseInt(monthSelect.value);
		const year = parseInt(yearSelect.value);
		Calendar.currentDate.month(month).year(year);
		
		if (Calendar.currentView === 'week') {
			Calendar.currentDate.startOf('month');
		}
		
		Calendar.loadData();
	}

	private static updateSelectors() {
		const m = d.get<HTMLSelectElement>('month-select');
		const y = d.get<HTMLSelectElement>('year-select');
		if (m) { m.value = Calendar.currentDate.month().toString(); }
		if (y) { y.value = Calendar.currentDate.year().toString(); }
	}

	private static updateActiveViewButton() {
		const monthBtn = d.get<HTMLButtonElement>('view-month-btn');
		const weekBtn = d.get<HTMLButtonElement>('view-week-btn');
		
		if (Calendar.currentView === 'month') {
			monthBtn?.classList.replace('btn-secondary', 'btn-primary');
			weekBtn?.classList.replace('btn-primary', 'btn-secondary');
		} else {
			weekBtn?.classList.replace('btn-secondary', 'btn-primary');
			monthBtn?.classList.replace('btn-primary', 'btn-secondary');
		}
	}

	private static render() {
		const container = d.get<HTMLElement>('calendar-days');
		const label = d.get<HTMLElement>('current-period-label');
		const monthlySummaryContainer = d.get<HTMLElement>('monthly-summary-container');
		if (!container) { return; }

		container.innerHTML = '';
		if (monthlySummaryContainer) { monthlySummaryContainer.innerHTML = ''; }
		
		if (Calendar.currentView === 'month') {
			label.innerText = Calendar.currentDate.format('MMMM YYYY');
			document.getElementById('calendar-grid-container')?.classList.remove('weekly-view');
		} else {
			const start = moment(Calendar.currentDate).startOf('week');
			const end = moment(Calendar.currentDate).endOf('week');
			label.innerText = `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
			document.getElementById('calendar-grid-container')?.classList.add('weekly-view');
		}

		const startDate = Calendar.currentView === 'month' ? 
			moment(Calendar.currentDate).startOf('month').startOf('week') : 
			moment(Calendar.currentDate).startOf('week');
		
		const endDate = Calendar.currentView === 'month' ? 
			moment(Calendar.currentDate).endOf('month').endOf('week') : 
			moment(Calendar.currentDate).endOf('week');

		let runningBalance = Calendar.startingBalance;
		const dayIter = moment(startDate);
		
		const fmtMoney = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

		// Monthly totals
		let monthlyTotalPnL = 0;
		let monthlyTotalTrades = 0;
		let monthStartBalance = 0;
		let hasSetMonthStartBalance = false;

		// Weekly tracking
		let weekPnL = 0;
		let weekTrades = 0;
		let weekStartBalance = 0;
		let hasSetWeekStartBalance = false;

		while (dayIter.isSameOrBefore(endDate)) {
			const isCurrentMonth = dayIter.month() === Calendar.currentDate.month();
			const isStartOfWeek = dayIter.day() === 0;
			const isEndOfWeek = dayIter.day() === 6;

			if (isStartOfWeek) {
				weekPnL = 0;
				weekTrades = 0;
				weekStartBalance = 0;
				hasSetWeekStartBalance = false;
			}

			const dateStr = dayIter.format('YYYY-MM-DD');
			const dayPnL = Calendar.dailyData.find(d => dayIter.isSame(moment(d.date), 'day'));
			const adjustments = Calendar.balanceAdjustments.filter(a => dayIter.isSame(moment(a.date), 'day'));

			// Adjust balance for the day (deposits/withdrawals)
			adjustments.forEach(a => runningBalance += parseFloat(a.amount));

			// Anchor the return-% denominator on the balance once the account is actually
			// funded — the first day in the period with a positive balance. The server now
			// supplies a correct start-of-range balance (manualBalance model), but an account
			// funded mid-period still opens the month/week at $0; without this we divide by $0
			// and show 0.00% on a profitable month (e.g. a deposit landing mid-month, or a
			// brand-new account funded partway through the period).
			if (isCurrentMonth && !hasSetMonthStartBalance && runningBalance > 0) {
				monthStartBalance = runningBalance;
				hasSetMonthStartBalance = true;
			}

			if (!hasSetWeekStartBalance && runningBalance > 0) {
				weekStartBalance = runningBalance;
				hasSetWeekStartBalance = true;
			}

			const dayDiv = document.createElement('div');
			dayDiv.className = 'calendar-day';
			if (Calendar.currentView === 'month' && !isCurrentMonth) {
				dayDiv.classList.add('not-current-month');
			}
			if (dayIter.isSame(moment(), 'day')) {
				dayDiv.classList.add('today');
			}

			let pnlVal = 0;
			let tradesCount = 0;
			if (dayPnL) {
				pnlVal = parseFloat(dayPnL.pnl);
				tradesCount = dayPnL.trades;
				dayDiv.classList.add(pnlVal >= 0 ? 'profit' : 'loss');
			} else {
				dayDiv.classList.add('neutral');
			}

			const percent = runningBalance > 0 ? (pnlVal / runningBalance) * 100 : 0;

			dayDiv.innerHTML = `
				<div class="day-number">${dayIter.date()}</div>
				${pnlVal !== 0 ? `
					<div class="day-pnl">${pnlVal > 0 ? '+' : ''}${fmtMoney(pnlVal)}</div>
					<div class="day-percent">${pnlVal > 0 ? '+' : ''}${percent.toFixed(2)}%</div>
					<div class="day-trades-count">${tradesCount} trade${tradesCount > 1 ? 's' : ''}</div>
				` : ''}
			`;

			dayDiv.addEventListener('click', () => {
				window.location.href = `/trade-data?startDate=${dateStr}&endDate=${dateStr}`;
			});

			container.appendChild(dayDiv);
			
			// Track stats
			weekPnL += pnlVal;
			weekTrades += tradesCount;
			
			if (isCurrentMonth) {
				monthlyTotalPnL += pnlVal;
				monthlyTotalTrades += tradesCount;
			}

			if (isEndOfWeek) {
				// Append Weekly Total Cell (8th column)
				const weekPercent = weekStartBalance > 0 ? (weekPnL / weekStartBalance) * 100 : 0;
				const totalDiv = document.createElement('div');
				totalDiv.className = 'weekly-total-cell';
				totalDiv.innerHTML = `
					<div class="weekly-total-value ${weekPnL >= 0 ? 'text-profit' : 'text-loss'}">
						${weekPnL >= 0 ? '+' : ''}${fmtMoney(weekPnL)}
					</div>
					<div class="weekly-total-label">${weekPercent >= 0 ? '+' : ''}${weekPercent.toFixed(2)}%</div>
					<div class="weekly-total-trades">${weekTrades} trade${weekTrades !== 1 ? 's' : ''}</div>
				`;
				container.appendChild(totalDiv);
			}

			// Add day's P&L to balance for the next day's calculation
			runningBalance += pnlVal;
			dayIter.add(1, 'day');
		}

		// Render Monthly Summary
		if (monthlySummaryContainer) {
			const monthlyPercent = monthStartBalance > 0 ? (monthlyTotalPnL / monthStartBalance) * 100 : 0;
			monthlySummaryContainer.innerHTML = `
				<div class="summary-stat-group">
					<div class="summary-stat-label">Monthly P&L</div>
					<div class="summary-stat-value ${monthlyTotalPnL >= 0 ? 'text-profit' : 'text-loss'}">
						${monthlyTotalPnL >= 0 ? '+' : ''}${fmtMoney(monthlyTotalPnL)}
					</div>
				</div>
				<div class="summary-stat-group">
					<div class="summary-stat-label">Trades</div>
					<div class="summary-stat-value">${monthlyTotalTrades}</div>
				</div>
				<div class="summary-stat-group">
					<div class="summary-stat-label">Return</div>
					<div class="summary-stat-value ${monthlyTotalPnL >= 0 ? 'text-profit' : 'text-loss'}">
						${monthlyTotalPnL >= 0 ? '+' : ''}${monthlyPercent.toFixed(2)}%
					</div>
				</div>
			`;
		}
	}
}
